import React, { useRef, useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import { GridEditorState, GridEditorActions } from '../../GridEditorPresenter';
import { LeafletMap, LeafletMapRef } from '../LeafletMap';
import { getDefaultProperties } from '../../utils/presenter.utils';
import { ElementAction, GridElement } from '../../types';
import { gpsToCanvas } from '../../utils/converter';
import { MeterRealtimeMetrics } from '../../hooks/useMeterRealtimeMetrics';
import { MeterLiveMetricsMap, LuminaireLiveMetricsMap } from '../../types/presenter.types';

interface GridCanvasProps {
  state: GridEditorState;
  actions: GridEditorActions;
  svgRef: React.RefObject<SVGSVGElement>;
  meterMetrics: MeterRealtimeMetrics;
  meterLiveMetrics: MeterLiveMetricsMap;
  luminaireLiveMetrics: LuminaireLiveMetricsMap;
  onVisibleDevicesChange?: (ids: Set<string>) => void;
}

export interface GridCanvasRef {
  setMapZoom: (zoom: number) => void;
  resetMapView: () => void;
  fitToElements: (elements: GridElement[]) => void;
}

export const GridCanvas = forwardRef<GridCanvasRef, GridCanvasProps>(({
  state,
  actions,
  svgRef,
  meterMetrics,
  meterLiveMetrics,
  luminaireLiveMetrics,
  onVisibleDevicesChange,
}, ref) => {
  const leafletMapRef = useRef<LeafletMapRef>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  useImperativeHandle(ref, () => ({
    setMapZoom: (zoom: number) => {
      if (leafletMapRef.current) {
        const leafletZoom = Math.max(1, Math.min(18, Math.round(zoom * 6 + 1)));
        leafletMapRef.current.setZoom(leafletZoom);
      }
    },
    resetMapView: () => {
      if (leafletMapRef.current) {
        const leafletZoom = Math.max(1, Math.min(18, Math.round(state.zoom * 6 + 1)));
        leafletMapRef.current.setView([state.mapCenter.lat, state.mapCenter.lng], leafletZoom);
      }
    },
    fitToElements: (elements: GridElement[]) => {
      if (leafletMapRef.current) {
        leafletMapRef.current.fitBoundsToElements(elements);
      }
    }
  }));

  const handleMapReady = () => {
    setIsMapReady(true);
  };
  
  const {
    elements,
    connections,
    selectedElement,
    selectedConnection,
    selectedTool,
    zoom,
    mapBackground,
    showGrid,
    mapCenter
  } = state;

  // Convertir les coordonnées de la carte pour Leaflet
  const leafletCenter: [number, number] = [mapCenter.lat, mapCenter.lng];
  // Convertir notre échelle de zoom (0.5-3) vers l'échelle Leaflet (1-18)
  const leafletZoom = Math.max(1, Math.min(18, Math.round(zoom * 6 + 1)));

  // Synchroniser les changements de mapCenter et zoom avec Leaflet
  useEffect(() => {
    if (leafletMapRef.current && isMapReady) {
      const leafletZoomValue = Math.max(1, Math.min(18, Math.round(zoom * 6 + 1)));
      leafletMapRef.current.setView([mapCenter.lat, mapCenter.lng], leafletZoomValue);
    }
  }, [mapCenter.lat, mapCenter.lng, zoom, isMapReady]);

  // Effet supplémentaire pour s'assurer que Leaflet est synchronisé après le montage
  useEffect(() => {
    if (isMapReady) {
      const timer = setTimeout(() => {
        if (leafletMapRef.current) {
          const leafletZoomValue = Math.max(1, Math.min(18, Math.round(zoom * 6 + 1)));
          leafletMapRef.current.setView([mapCenter.lat, mapCenter.lng], leafletZoomValue);
        }
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [mapCenter.lat, mapCenter.lng, zoom, isMapReady]);

  // Gestionnaires d'événements pour Leaflet
  const handleMapClick = (lat: number, lng: number) => {
    console.log('🗺️ Map click detected:', { lat, lng, selectedTool, elementsCount: elements.length });
    
    // Ne pas créer d'éléments avec les outils d'interaction
    if (['select', 'move', 'connect'].includes(selectedTool)) {
      console.log('🚫 Tool prevents element creation:', selectedTool);
      return;
    }
    
    console.log('✅ Tool allows element creation, proceeding...');
    
    // Convertir les coordonnées GPS en coordonnées canvas en utilisant les bounds globales
    const canvasCoords = gpsToCanvas(lat, lng, state.globalGpsBounds);
    const x = canvasCoords.x;
    const y = canvasCoords.y;
    
    console.log('📍 Coordinates converted:', { x, y, globalGpsBounds: state.globalGpsBounds });
    
    // Créer un nouvel élément directement
    const newElement = {
      id: `e${Date.now()}`,
      type: selectedTool as 'power-plant' | 'pole' | 'meter' | 'transformer',
      x,
      y,
      name: `${selectedTool} ${elements.length + 1}`,
      properties: getDefaultProperties(selectedTool),
      status: 'ACTIVE' as const,
      lights: selectedTool === 'pole' ? [] : undefined,
      gpsCoordinates: { latitude: lat, longitude: lng },
      action: ElementAction.TO_CREATE
    };
    
    console.log('🆕 Creating new element:', newElement);
    
    // Ajouter le nouvel élément et le sélectionner
    actions.handleElementsAdd([newElement]);
    actions.setSelectedElement(newElement);
    
    console.log('✅ Element added and selected');
  };

  const handleElementClick = (element: any) => {
    // Désélectionner la connexion si on clique sur un élément
    actions.setSelectedConnection(null);
    
    // Si l'outil est 'connect', gérer la création de connexion
    if (state.selectedTool === 'connect') {
      if (!state.connectionStart) {
        // Premier clic : démarrer une connexion depuis cet élément
        actions.setConnectionStart(element.id);
        actions.setSelectedElement(element);
      } else if (state.connectionStart !== element.id) {
        // Deuxième clic : compléter la connexion vers cet élément
        const newConnection = {
          id: `c${Date.now()}`,
          name: '3x150+70', // Default cable name
          fromId: state.connectionStart,
          toId: element.id,
          type: 'power-line' as const,
          voltage: 400,
          resistance: 0.2,
          length: 100,
          status: 'ACTIVE' as const,
          action: ElementAction.TO_CREATE
        };
        actions.handleConnectionsAdd([newConnection]);
        actions.setConnectionStart(null);
        actions.setSelectedElement(element);
      }
      // Si on clique deux fois sur le même élément, annuler la connexion
      else {
        actions.setConnectionStart(null);
        actions.setSelectedElement(element);
      }
    } else {
      // Pour les autres outils, sélectionner directement l'élément
      actions.setSelectedElement(element);
    }
  };

  const handleConnectionClick = (connection: any) => {
    actions.setSelectedConnection(connection);
    // Désélectionner l'élément si on clique sur une connexion
    actions.setSelectedElement(null);
  };

  const handleStreetLightClick = (light: any, pole: GridElement) => {
    console.log('Street light clicked:', light, 'on pole:', pole);
    // Ici on peut ajouter la logique pour sélectionner ou afficher des détails sur la street light
  };

  const handleElementMove = (elementId: string, lat: number, lng: number) => {
    // Convertir les coordonnées GPS en coordonnées canvas en utilisant les bounds globales
    const canvasCoords = gpsToCanvas(lat, lng, state.globalGpsBounds);
    
    actions.updateElement(elementId, { 
      x: canvasCoords.x, 
      y: canvasCoords.y, 
      gpsCoordinates: { latitude: lat, longitude: lng } 
    });
  };

  return (
    <div className="flex-1 relative min-w-0 overflow-hidden">
      <div className="absolute inset-0">
        <LeafletMap
          ref={leafletMapRef}
          elements={elements}
          connections={connections}
          selectedElement={selectedElement}
          selectedConnection={selectedConnection}
          onElementClick={handleElementClick}
          onConnectionClick={handleConnectionClick}
          onMapClick={handleMapClick}
          onElementMove={handleElementMove}
          onStreetLightClick={handleStreetLightClick}
          center={leafletCenter}
          zoom={leafletZoom}
          mapStyle={mapBackground === 'none' ? 'osm' : mapBackground as 'osm' | 'satellite' | 'terrain' | 'street'}
          showGrid={showGrid}
          className="h-full w-full"
          onMapReady={handleMapReady}
          isEditMode={state.isEditMode}
          meterMetrics={meterMetrics}
          meterLiveMetrics={meterLiveMetrics}
          luminaireLiveMetrics={luminaireLiveMetrics}
          metricsVisibility={state.metricsVisibility}
          onVisibleDevicesChange={onVisibleDevicesChange}
        />

        {/* Data loading overlay */}
        {(() => {
          if (state.isLoadingNodes || state.isLoadingLinks || state.isLoadingStreetLights) {
            console.log('🔄 Loading states:', {
              isLoadingNodes: state.isLoadingNodes,
              isLoadingLinks: state.isLoadingLinks,
              isLoadingStreetLights: state.isLoadingStreetLights
            });
          }
          return null;
        })()}
        {(state.isLoadingNodes || state.isLoadingLinks || state.isLoadingStreetLights) && (
          <div
            className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center text-white z-[9999] backdrop-blur-sm"
          >
            <div className="text-center bg-gray-900 bg-opacity-80 rounded-2xl p-8 shadow-2xl border border-gray-700">
              {/* Simple loading spinner */}
              <div className="relative mb-6">
                <div className="w-12 h-12 mx-auto">
                  <div className="w-12 h-12 border-4 border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold mb-2">Chargement des données</h3>
              <p className="text-gray-300 mb-4">Récupération des éléments du plan en cours...</p>
              
              {/* Loading progress indicators */}
              <div className="space-y-2">
                {state.isLoadingNodes && (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-gray-300">Chargement des nœuds</span>
                  </div>
                )}
                {state.isLoadingLinks && (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                    <span className="text-sm text-gray-300">Chargement des connexions</span>
                  </div>
                )}
                {state.isLoadingStreetLights && (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                    <span className="text-sm text-gray-300">Chargement des éclairages</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Save loading overlay */}
        {state.isSavingChanges && (
          <div
            className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center text-white z-[9998] backdrop-blur-sm"
          >
            <div className="text-center bg-gray-800 bg-opacity-90 rounded-xl p-6 shadow-xl border border-gray-600">
              {/* Save loading spinner */}
              <div className="relative mb-4">
                <div className="w-10 h-10 mx-auto">
                  <div className="w-10 h-10 border-3 border-gray-500 border-t-green-400 rounded-full animate-spin"></div>
                </div>
              </div>
              
              <h4 className="text-lg font-medium mb-2">Sauvegarde en cours</h4>
              <p className="text-gray-300 text-sm mb-3">Enregistrement des modifications...</p>
              
              {/* Progress indicator if available */}
              {state.saveProgress && state.saveProgress.total > 0 && (
                <div className="w-48 mx-auto">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>{state.saveProgress.currentStep}</span>
                    <span>{state.saveProgress.current}/{state.saveProgress.total}</span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-green-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${state.saveProgress.percentage}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
